import userService from "./userService";

/**
 * Cosa puo' vedere, in questa applicazione, chi ha un certo ruolo nello spazio attivo.
 *
 * ATTENZIONE A COSA E' E A COSA NON E'
 * Questa non e' la sicurezza: la sicurezza sta sul server, in FiltroPermessiRuolo, ed e' quella
 * che conta. Questo file serve a non mostrare porte che si aprirebbero su un errore — una voce di
 * menu che porta a un 403 e' peggio di una voce che non c'e'. La tabella qui sotto rispecchia
 * quella del server: se una delle due cambia, va cambiata anche l'altra, e a sbagliare si ottiene
 * un menu incoerente, mai un accesso in piu'.
 *
 * Le aree e i nomi sono gli stessi di it.zoelia.core.security.AreaApplicativa e MappaAree.
 */

const AREE = {
  ACCOUNT: "ACCOUNT",
  SQUADRA: "SQUADRA",
  STRUTTURE: "STRUTTURE",
  PULIZIE: "PULIZIE",
  CRUSCOTTO: "CRUSCOTTO",
  PRENOTAZIONI: "PRENOTAZIONI",
  CALENDARIO: "CALENDARIO",
  TARIFFE: "TARIFFE",
  MESSAGGI: "MESSAGGI",
  OSPITI: "OSPITI",
  DISPOSITIVI: "DISPOSITIVI",
  ECONOMICI: "ECONOMICI",
  INTEGRAZIONI: "INTEGRAZIONI",
  AMMINISTRAZIONE: "AMMINISTRAZIONE",
};

const TUTTE = Object.values(AREE);

// Le stesse quattro righe che stanno sul server. Titolare e amministratore passano ovunque: e'
// il comportamento di ogni utente di oggi, ed e' cio' che rende questo lavoro privo di regressioni.
const PER_RUOLO = {
  TITOLARE: TUTTE,
  AMMINISTRATORE: TUTTE,
  COLLABORATORE: [
    AREE.ACCOUNT, AREE.STRUTTURE, AREE.CRUSCOTTO, AREE.PRENOTAZIONI, AREE.CALENDARIO,
    AREE.TARIFFE, AREE.MESSAGGI, AREE.OSPITI, AREE.DISPOSITIVI, AREE.PULIZIE,
  ],
  GOVERNANTE: [AREE.ACCOUNT, AREE.PULIZIE],
  // Niente ECONOMICI: la rendita e gli account di incasso sono i conti del gestore, non quelli
  // della casa. Gli importi delle sue prenotazioni li vede lo stesso, perche' stanno in
  // PRENOTAZIONI.
  PROPRIETARIO: [AREE.ACCOUNT, AREE.STRUTTURE, AREE.PRENOTAZIONI, AREE.CALENDARIO],
};

// Quello che un ruolo puo' MODIFICARE, che non e' sempre quello che puo' vedere.
//
// Il collaboratore legge le strutture — ogni schermata ha un selettore di case, e calendario,
// prenotazioni e messaggi hanno bisogno dei nomi e dei colori — ma non le scrive: nel modulo di
// una struttura ci sono le credenziali del portale della Polizia, la tassa di soggiorno, le
// impostazioni di rendita e l'account di incasso. E aggiungere o togliere una casa e' una
// decisione sul portafoglio del gestore, non lavoro operativo.
// Il proprietario non modifica niente: guarda le sue case, il calendario e le prenotazioni.
const SCRITTURA_PER_RUOLO = {
  TITOLARE: TUTTE,
  AMMINISTRATORE: TUTTE,
  COLLABORATORE: [
    AREE.ACCOUNT, AREE.CRUSCOTTO, AREE.PRENOTAZIONI, AREE.CALENDARIO,
    AREE.TARIFFE, AREE.MESSAGGI, AREE.OSPITI, AREE.DISPOSITIVI, AREE.PULIZIE,
  ],
  GOVERNANTE: [AREE.ACCOUNT, AREE.PULIZIE],
  PROPRIETARIO: [AREE.ACCOUNT],
};

// I percorsi dell'applicazione, dal piu' specifico al piu' generico: vince il primo che combacia.
// L'ordine conta — /settings/squadra e' la squadra, non le impostazioni generiche.
const PERCORSI = [
  // Il secondo fattore e il proprio profilo sono dell'area ACCOUNT, che ogni ruolo ha.
  //
  // Senza queste due righe cadrebbero nel ripiego AMMINISTRAZIONE, e per un collaboratore, una
  // governante o un proprietario succederebbe questo: la guardia del secondo fattore li manda
  // su /secondo-fattore, la guardia del ruolo dice che non ci possono stare e li rimanda alla
  // loro pagina iniziale, la guardia del secondo fattore li rimanda su /secondo-fattore — e non
  // si esce. Non entrerebbero affatto.
  ["/secondo-fattore", AREE.ACCOUNT],
  ["/hub", AREE.ACCOUNT],
  ["/accessi", AREE.ACCOUNT],
  // Il portale dei proprietari (F6): la vista di se' del proprietario, come il proprio profilo. E' la stessa riga
  // di MappaAree (/api/v1/portale-proprietario -> ACCOUNT); la barriera che conta e' sul server, che trova il
  // proprietario dalla persona a ogni richiesta e risponde 404 a tutti gli altri.
  ["/portale", AREE.ACCOUNT],
  ["/profile", AREE.ACCOUNT],
  // La contabilita' del nucleo (Fase C, faseC-registri.md §3): il registro incassi, i conti del gestore come /api/v1/pagamenti
  // (MappaAree -> ECONOMICI), prima di "/settings" come chiede la revisione (faseC-revisione.md §2.2 c). Le pagine della
  // contabilita' del modulo stanno sotto /gestione/contabilita, gia' ECONOMICI qui sotto.
  ["/contabilita", AREE.ECONOMICI],
  ["/settings/squadra", AREE.SQUADRA],
  ["/settings/notifiche", AREE.SQUADRA],
  ["/settings/stripe-accounts", AREE.ECONOMICI],
  // Il modulo di gestione professionale: sono i conti del gestore, prima di "/settings" che e' ACCOUNT.
  ["/settings/gestione-professionale", AREE.ECONOMICI],
  // Il catalogo dei servizi aggiuntivi (Fase B): i conti del gestore, come gli account Stripe e il modulo, prima di "/settings"
  // che e' ACCOUNT. E' la riga di MappaAree (/api/v1/servizi-aggiuntivi -> ECONOMICI): senza, il collaboratore vedrebbe il
  // riquadro nelle impostazioni ed entrerebbe nella pagina, per poi trovare un 403.
  ["/settings/servizi-aggiuntivi", AREE.ECONOMICI],
  // Le condizioni delle dirette (Fase D, faseD-condizioni.md K1): acconto, penali e rimborsi, cioe' i conti del gestore, come
  // il catalogo dei servizi, prima di "/settings" che e' ACCOUNT. E' la riga di MappaAree (/api/v1/condizioni-dirette ->
  // ECONOMICI): il collaboratore non vede il riquadro e non entra nella pagina.
  ["/settings/condizioni-dirette", AREE.ECONOMICI],
  // I conti di incasso (Fase G, faseG-incassi.md §5.1): dove arrivano i soldi, cioe' i conti del gestore, accanto agli account
  // Stripe, prima di "/settings" che e' ACCOUNT. E' la riga di MappaAree (/api/v1/conti-incasso -> ECONOMICI): il collaboratore
  // non vede il riquadro e non entra nella pagina.
  ["/settings/conti-incasso", AREE.ECONOMICI],
  ["/settings/providers", AREE.INTEGRAZIONI],
  ["/settings/canali-messaggistica", AREE.INTEGRAZIONI],
  ["/settings/pricelabs", AREE.INTEGRAZIONI],
  ["/settings/chiavi-api", AREE.INTEGRAZIONI],
  ["/settings/test-funzionalita", AREE.AMMINISTRAZIONE],
  // Il contenitore delle impostazioni resta raggiungibile da tutti: dentro mostra solo le
  // schede permesse, e per un ruolo limitato e' comunque fuori dal menu.
  ["/settings", AREE.ACCOUNT],
  ["/pulizie", AREE.PULIZIE],
  ["/properties", AREE.STRUTTURE],
  ["/preventivi", AREE.PRENOTAZIONI],
  ["/reservations", AREE.PRENOTAZIONI],
  ["/pms", AREE.CALENDARIO],
  ["/messages", AREE.MESSAGGI],
  ["/modelli-messaggio", AREE.MESSAGGI],
  ["/contract-templates", AREE.OSPITI],
  ["/checkin-dashboard", AREE.OSPITI],
  ["/clienti", AREE.OSPITI],
  ["/devices", AREE.DISPOSITIVI],
  ["/access-links", AREE.DISPOSITIVI],
  ["/rental-report", AREE.ECONOMICI],
  // L'anagrafica dei proprietari: ci sono IBAN e codici fiscali, quindi e' dei conti del gestore
  // come la rendita. La tendina del proprietario nel modulo della struttura non passa di qui: usa
  // /proprietari/opzioni, che il server apre a chi scrive le STRUTTURE.
  ["/proprietari", AREE.ECONOMICI],
  // Mandati, rendiconti e ritenuta del modulo professionale (F2): come la rendita, conti del gestore.
  ["/gestione", AREE.ECONOMICI],
  ["/task-queue", AREE.AMMINISTRAZIONE],
  ["/admin", AREE.AMMINISTRAZIONE],
  ["/account", AREE.ACCOUNT],
];

// Dove atterra chi entra, quando la home non fa per lui. Una governante mandata sul cruscotto
// vedrebbe una pagina che il server le nega: sembrerebbe un guasto, non un permesso mancante.
// Il proprietario sul calendario: e' la domanda con cui entra (chi arriva, quando), e nell'app
// /properties non e' nemmeno nella barra in basso.
const PAGINA_INIZIALE = {
  GOVERNANTE: "/pulizie",
  PROPRIETARIO: "/pms",
};

// Chi vede la campanella. Le notifiche sono dello spazio, non della persona: una sola lista per
// tutti, e chi la legge la puo' anche segnare letta o svuotare per tutta la squadra. Stessa
// regola di Appartenenza.vedeGliAvvisiDelloSpazio sul server.
const RUOLI_CON_AVVISI = ["TITOLARE", "AMMINISTRATORE", "COLLABORATORE"];

// I permessi di chi non si sa ancora chi sia: tutto falso. Congelato perche' e' lo stesso oggetto
// per tutte le pagine, e una che lo modificasse per sbaglio lo cambierebbe a tutte le altre.
const NESSUN_PERMESSO = Object.freeze({
  ruolo: null,
  legge: Object.freeze({}),
  scrive: Object.freeze({}),
});

export const permessiService = {
  AREE,
  NESSUN_PERMESSO,

  /** L'area di un percorso dell'applicazione. Ripiego prudente: l'area piu' chiusa. */
  areaDi(percorso) {
    if (!percorso || percorso === "/") return AREE.CRUSCOTTO;
    const trovato = PERCORSI.find(([prefisso]) => percorso.startsWith(prefisso));
    return trovato ? trovato[1] : AREE.AMMINISTRAZIONE;
  },

  /** Le aree consentite a un ruolo. Ruolo sconosciuto o assente: nessuna area. */
  areeDi(ruolo) {
    return PER_RUOLO[ruolo] || [];
  },

  /** Il ruolo di chi sta guardando, nello spazio attivo. */
  async ruolo() {
    const profilo = await userService.getProfile();
    return profilo?.ruolo || null;
  },

  /** Se il ruolo indicato puo' leggere quell'area. */
  puoLeggereArea(ruolo, area) {
    // Nessun ruolo: e' chi non appartiene ad alcuno spazio e ha solo un invito da accettare.
    // Non gli si nega la navigazione, sarebbe un muro senza spiegazione; le pagine si
    // troveranno vuote e /account/me racconta al frontend cosa sta succedendo.
    if (!ruolo) return true;
    return this.areeDi(ruolo).includes(area);
  },

  /** Se il ruolo indicato puo' modificare quell'area. Ruolo assente: si', come per la lettura. */
  puoScrivereArea(ruolo, area) {
    if (!ruolo) return true;
    return (SCRITTURA_PER_RUOLO[ruolo] || []).includes(area);
  },

  /** Se il ruolo indicato puo' aprire quel percorso. */
  puoAprire(ruolo, percorso) {
    return this.puoLeggereArea(ruolo, this.areaDi(percorso));
  },

  /**
   * Se il ruolo indicato puo' MODIFICARE cio' che sta su quel percorso.
   *
   * Serve a non mostrare pulsanti che finirebbero in un 403: le azioni non permesse spariscono,
   * non si disabilitano — un pulsante grigio e' un invito a chiedere perche'.
   */
  puoScrivere(ruolo, percorso) {
    return this.puoScrivereArea(ruolo, this.areaDi(percorso));
  },

  /**
   * Cosa puo' leggere e cosa puo' modificare chi sta guardando, area per area.
   *
   * E' la forma che usano le pagine: `permessi.scrive.OSPITI` nel template invece di una
   * domanda sul ruolo ripetuta in ogni componente. Le pagine partono da NESSUN_PERMESSO e
   * assegnano questo in mounted, prima delle chiamate che ne dipendono.
   *
   * Profilo non arrivato (rete, errore): tutto falso. E' la scelta prudente — per un momento
   * mancano dei pulsanti anche al titolare, ma nessuno ne vede uno che il server gli negherebbe.
   * Profilo senza ruolo (nessuno spazio, solo inviti): tutto vero, come puoAprire.
   */
  async permessiDiChiGuarda() {
    const profilo = await userService.getProfile();
    if (!profilo) return NESSUN_PERMESSO;
    const ruolo = profilo.ruolo || null;
    const legge = {};
    const scrive = {};
    TUTTE.forEach((area) => {
      legge[area] = this.puoLeggereArea(ruolo, area);
      scrive[area] = this.puoScrivereArea(ruolo, area);
    });
    return { ruolo, legge, scrive };
  },

  /**
   * Se il ruolo vede le notifiche dello spazio (la campanella in alto).
   *
   * Non e' un'area: le notifiche stanno in ACCOUNT, che ogni ruolo legge e scrive, e con quella
   * sola regola una governante o un proprietario le potrebbero svuotare per tutta la squadra.
   * Ruolo assente: si', come oggi.
   */
  vedeAvvisiDelloSpazio(ruolo) {
    if (!ruolo) return true;
    return RUOLI_CON_AVVISI.includes(ruolo);
  },

  /** Dove mandare chi entra, in base al ruolo che ha. */
  paginaIniziale(ruolo) {
    return PAGINA_INIZIALE[ruolo] || "/";
  },
};

export default permessiService;
